/**
 * Gestaffelter synthetischer Rabatt (Tiered Display) fuer Shaker.
 *
 * Architektur: Der Prozentwert kommt AUSSCHLIESSLICH vom Server
 * (snippets/synthetic-discount-percent.liquid — einzige Wahrheitsquelle;
 * aktuell statisch aus den Settings, unabhaengig vom Warenkorb — keine
 * Mengenstufen mehr, die laufen ueber Upsell Shark). Der Wert steht als
 * [data-tier-state data-tier-percent="…"] im Markup:
 *   - initial im Layout (theme.liquid) fuer den Seitenaufbau,
 *   - im Cart-Drawer, den ALLE Cart-Flows des Themes (Quick-Add, PDP-Add,
 *     Mengenaenderung, Entfernen) bei jeder Mutation ueber die Section
 *     Rendering API ersetzen. Die Section-HTML kommt in derselben Antwort
 *     wie die Mutation selbst — atomar konsistent, kein Read-after-Write,
 *     keine clientseitige Zaehlung, keine Produkt-Tag-Lookups.
 *
 * Dieses Script liest nur den jeweils zuletzt eingetroffenen Serverwert und
 * schreibt markierte Elemente um: [data-tier-price] am .price-Span
 * (data-price = unreduzierte Basis, data-tier-reduced = zuletzt dargestellter
 * reduzierter Preis fuer idempotente Text-Ersetzung) und [data-tier-badge]
 * am Spar-Badge (nur .badge-label, das Icon bleibt erhalten).
 *
 * Zusammenspiel: Stufen-Elemente ([data-tier-price]) verwaltet AUSSCHLIESSLICH
 * dieses Script — discount-sitewide.js ueberspringt sie. Ist ein prozentualer
 * Bestellrabatt-Code aktiv (window.SitewideDiscount), wird er hier
 * multiplikativ auf den Stufenpreis aufgeschlagen (Basis x (1-Stufe%) x
 * (1-Code%)); der Code-Schritt zieht den auf ganze Cents abgeschnittenen
 * Rabattbetrag ab, wie es auch der Checkout rechnet. Das Badge zeigt
 * weiterhin nur die Stufe.
 */
(function () {
  'use strict';

  var config = (window.theme && window.theme.tieredDiscount) || {};
  var currentPercent = null; // null = noch kein Serverwert uebernommen

  /* ---------------------------------------------------------------------- *
   *  Money-Formatierung (Shopify-Standard-Algorithmus, wie discount-sitewide)
   * ---------------------------------------------------------------------- */
  function moneyFormat() {
    var s = (window.theme && window.theme.settings) || {};
    if (s.currency_code_enabled && s.money_format_with_currency) {
      return s.money_format_with_currency;
    }
    return s.money_format || s.money_with_currency_format || '{{amount}} €';
  }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '{{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    var value = '';
    var match = formatString.match(placeholderRegex);
    var token = match ? match[1] : 'amount';
    switch (token) {
      case 'amount': value = formatWithDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_with_space_separator': value = formatWithDelimiters(cents, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = formatWithDelimiters(cents, 2, ' ', '.'); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.'); break;
      case 'amount_no_decimals_with_space_separator': value = formatWithDelimiters(cents, 0, ' '); break;
      case 'amount_with_apostrophe_separator': value = formatWithDelimiters(cents, 2, "'"); break;
      default: value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  /* ---------------------------------------------------------------------- *
   *  Serverwert uebernehmen
   * ---------------------------------------------------------------------- */
  function adoptState(el) {
    var raw = parseFloat(el.getAttribute('data-tier-percent'));
    var percent = (!isNaN(raw) && raw > 0 && raw < 100) ? raw : 0;
    if (currentPercent !== null && percent === currentPercent) return false;
    var hadValue = currentPercent !== null;
    currentPercent = percent;
    if (hadValue) {
      document.dispatchEvent(new CustomEvent('tiered-discount:changed', {
        detail: { percent: currentPercent }
      }));
    }
    // Beim allerersten Uebernehmen hat der Server die Seite bereits mit
    // genau diesem Wert gerendert — kein Rewrite noetig.
    return hadValue;
  }

  /* ---------------------------------------------------------------------- *
   *  Anzeige umschreiben
   * ---------------------------------------------------------------------- */
  function codePercent() {
    return (window.SitewideDiscount && window.SitewideDiscount.getPercent()) || 0;
  }

  // Betrags-Suche mit Ziffern-Grenze: "9,99 €" darf nicht in "19,99 €" matchen.
  function findMoney(text, money) {
    var idx = text.indexOf(money);
    while (idx !== -1) {
      var prev = idx > 0 ? text.charAt(idx - 1) : '';
      if (!/[0-9.,]/.test(prev)) return idx;
      idx = text.indexOf(money, idx + 1);
    }
    return -1;
  }

  function applyPrice(el) {
    var orig = parseInt(el.getAttribute('data-price'), 10);
    if (isNaN(orig) || orig <= 0) return;

    var ins = el.querySelector('ins');
    var amount = ins && ins.querySelector('.amount');
    if (!amount) return;

    var last = parseInt(el.getAttribute('data-tier-reduced'), 10);
    if (isNaN(last)) last = orig;

    // Stufenpreis wie serverseitig gerendert und wie die Upsell-Shark-
    // Discount-Function: Rabattbetrag auf ganze Cents abschneiden, ganzzahlig
    // in Hundertstel-Prozent (exakt gleich zu product-price.liquid).
    // Dann ein evtl. aktiver Bestellrabatt-Code obendrauf. Der Checkout
    // schneidet auch den Code-Rabattbetrag auf ganze Cents ab (kein Runden);
    // Epsilon faengt Float-Drift knapp unter einer Cent-Grenze ab.
    var reduced = currentPercent > 0
      ? orig - Math.floor(orig * Math.round(currentPercent * 100) / 10000)
      : orig;
    var code = codePercent();
    if (code > 0 && code < 100) {
      reduced = reduced - Math.floor(reduced * code / 100 + 1e-9);
    }

    var format = moneyFormat();
    var newMoney = formatMoney(reduced, format);
    var text = amount.textContent;

    // Idempotenz gegen den DOM pruefen, nicht gegen data-tier-reduced:
    // fremde Scripts koennen den Preistext ueberschrieben haben, waehrend das
    // Attribut noch den Zielwert traegt. Nur schreiben, wenn der Zielbetrag
    // tatsaechlich fehlt — dadurch heilt sich die Anzeige bei jeder Mutation
    // selbst und Schreib-Schleifen mit dem MutationObserver sind unmoeglich.
    if (findMoney(text, newMoney) === -1) {
      var lastMoney = formatMoney(last, format);
      var idx = findMoney(text, lastMoney);
      // "ab …"-Praefixe bleiben erhalten, indem nur der Geldbetrag ersetzt wird.
      amount.textContent = idx !== -1
        ? text.slice(0, idx) + newMoney + text.slice(idx + lastMoney.length)
        : newMoney;
    }

    var del = el.querySelector('del');
    if (reduced < orig) {
      if (!del) {
        del = document.createElement('del');
        var delAmount = document.createElement('span');
        delAmount.className = 'amount';
        delAmount.textContent = formatMoney(orig, format);
        del.appendChild(delAmount);
        ins.parentNode.insertBefore(del, ins);
      }
      if (amount.className.indexOf('discounted') === -1) {
        amount.className = (amount.className + ' discounted').trim();
      }
    } else if (del) {
      del.parentNode.removeChild(del);
      amount.className = amount.className.replace(/\bdiscounted\b/, '').trim();
    }

    if (reduced !== last) {
      el.setAttribute('data-tier-reduced', String(reduced));
    }
  }

  function applyBadge(badge) {
    if (currentPercent > 0) {
      badge.style.display = '';
      // Kunde sieht immer die gerundete Prozentzahl, nie den Dezimalwert.
      // Nur das Label ersetzen, damit das Discount-Icon im Badge erhalten bleibt.
      if (config.badgeType !== 'sale' && config.badgeLabel) {
        var target = badge.querySelector('.badge-label') || badge;
        var html = config.badgeLabel.replace('__PCT__', Math.round(currentPercent) + '%');
        // Nur bei Aenderung schreiben (verhindert Observer-Schleifen).
        if (target.innerHTML !== html) target.innerHTML = html;
      }
    } else {
      badge.style.display = 'none';
    }
  }

  function processAll(root) {
    if (currentPercent === null) return;
    var scope = root && root.querySelectorAll ? root : document;
    if (scope !== document && scope.matches) {
      if (scope.matches('[data-tier-price]')) applyPrice(scope);
      if (scope.matches('[data-tier-badge]')) applyBadge(scope);
    }
    var prices = scope.querySelectorAll('[data-tier-price]');
    for (var i = 0; i < prices.length; i++) applyPrice(prices[i]);
    var badges = scope.querySelectorAll('[data-tier-badge]');
    for (var j = 0; j < badges.length; j++) applyBadge(badges[j]);
  }

  /* ---------------------------------------------------------------------- *
   *  Init
   * ---------------------------------------------------------------------- */
  function init() {
    // Initialwert aus dem serverseitig gerenderten Marker (Layout/Drawer).
    var initial = document.querySelector('[data-tier-state]');
    if (initial) adoptState(initial);

    // Jede Cart-Mutation ersetzt den Drawer-Inhalt inklusive frischem Marker;
    // der Observer uebernimmt den neuen Wert und gleicht die ganze Seite an.
    // Nachgeladene Inhalte ohne Stufenwechsel (Quick-View, Infinite-Scroll)
    // werden lokal auf den aktuellen Stand gebracht.
    if (typeof MutationObserver !== 'undefined') {
      var observer = new MutationObserver(function (mutations) {
        var stateNode = null;
        var scopes = [];
        for (var i = 0; i < mutations.length; i++) {
          // Aenderungen INNERHALB markierter Elemente reparieren — z. B. wenn
          // ein fremdes Script (discount-sitewide, Apps) den Preistext
          // ueberschreibt. applyPrice/applyBadge schreiben nur bei Abweichung,
          // daher entsteht keine Schleife mit den eigenen Writes.
          var target = mutations[i].target;
          if (target && target.nodeType === 1 && target.closest) {
            var host = target.closest('[data-tier-price], [data-tier-badge]');
            if (host && scopes.indexOf(host) === -1) scopes.push(host);
          }
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var node = added[j];
            if (node.nodeType !== 1) continue;
            if (node.matches && node.matches('[data-tier-state]')) {
              stateNode = node;
            } else if (node.querySelectorAll) {
              var states = node.querySelectorAll('[data-tier-state]');
              if (states.length) stateNode = states[states.length - 1];
            }
            if (node.matches && node.matches('[data-tier-price], [data-tier-badge]')) {
              scopes.push(node.parentNode || node);
            } else if (node.querySelector && node.querySelector('[data-tier-price], [data-tier-badge]')) {
              scopes.push(node);
            }
          }
        }
        if (stateNode && adoptState(stateNode)) {
          processAll(document);
        } else {
          for (var k = 0; k < scopes.length; k++) processAll(scopes[k]);
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    // Variantenwechsel rendert die Preis-Section serverseitig neu -> angleichen.
    document.addEventListener('product:variant-change', function () {
      setTimeout(function () { processAll(document); }, 0);
    });

    // Code-Rabatt geaendert (aktiviert/entfernt) -> kombinierten Preis neu
    // rechnen.
    document.addEventListener('sitewide-discount:changed', function () {
      setTimeout(function () { processAll(document); }, 0);
    });
  }

  // Oeffentliche API fuer externe Verbraucher.
  window.TieredDiscount = {
    getPercent: function () { return currentPercent === null ? 0 : currentPercent; }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
